import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  IconButton,
  IconType,
} from '../pages/Licenses/edit/components/IconButton';

interface CloudinaryUploadWidgetProps {
  initialUrl?: string;
  initialFilename?: string;
  isImageOnly?: boolean;
  isViewOnly?: boolean;
  resourceType?: string;
  clientAllowedFormats?: string[];
  compact?: boolean;
  onImageUpload?: (imageInfo: any) => void;
  onDelete?: () => void;
}

const UploadImageButton = styled.button<{ compact?: boolean }>`
  cursor: pointer;
  box-sizing: border-box;
  display: -webkit-inline-box;
  display: -webkit-inline-flex;
  display: -ms-inline-flexbox;
  display: inline-flex;
  -webkit-align-items: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  width: -webkit-fit-content;
  width: -moz-fit-content;
  width: fit-content;
  border-radius: 4px;
  border: none;
  -webkit-text-decoration: none;
  text-decoration: none;
  font-weight: 600;
  line-height: 1.5;
  -webkit-transition: all 0.2s ease-out;
  transition: all 0.2s ease-out;
  -webkit-transition-delay: 0.05s;
  transition-delay: 0.05s;
  background: #102e8e;
  color: #ffffff;
  font-size: ${({ compact }) => compact ? '11px' : '14px'};
  max-height: ${({ compact }) => compact ? '30px' : '48px'};
  min-height: ${({ compact }) => compact ? '30px' : '48px'};
  padding: ${({ compact }) => compact ? '0 12px' : '0 24px'};
  text-transform: uppercase;
  -webkit-letter-spacing: 1px;
  -moz-letter-spacing: 1px;
  -ms-letter-spacing: 1px;
  letter-spacing: 1px;
`;

const IconButtonContainer = styled.div`
  min-width: auto !important;
  min-height: auto !important;
  padding: 0 !important;
  width: 24px !important;
  height: 24px !important;
  margin-right: 8px !important;
  flex-shrink: 0 !important;
  -webkit-box-pack: center;
  justify-content: center;
  cursor: pointer;
  box-sizing: border-box;
  display: inline-flex;
  -webkit-box-align: center;
  align-items: center;
  width: fit-content;
  border-radius: 4px;
  border: none;
  text-decoration: none;
  font-weight: 600;
  line-height: 1.5;
  transition: all 0.2s ease-out 0.05s;
  background: rgb(16, 46, 142);
  color: rgb(255, 255, 255);
  font-size: 14px;
  max-height: 48px;
  min-height: 48px;
  padding: 0px 24px;
  letter-spacing: 1px;
  margin-bottom: 14px;
  margin-left: 14px;
  transform: scale(0.7);
  border-radius: 8px;
  overflow: hidden;
  padding-left: 7px !important;
`;

const CloudinaryUploadWidget = (props: CloudinaryUploadWidgetProps) => {
  const {
    onImageUpload,
    onDelete,
    initialUrl,
    initialFilename,
    isImageOnly,
    isViewOnly,
    resourceType,
    clientAllowedFormats,
    compact,
  } = props;
  const [imageInfo, setImageInfo] = useState<any>(null);
  const widgetRef = useRef<any>(null);
  const onImageUploadRef = useRef(onImageUpload);
  useEffect(() => { onImageUploadRef.current = onImageUpload; });

  const { t } = useTranslation();
  const setupWidget = () => {
    if (isViewOnly) {
      return;
    }
    const myWidget = (window as any).cloudinary.createUploadWidget(
      {
        cloudName: 'dtg18yyuk',
        uploadPreset: 'y1wflviv',
        apiKey: '271483465517829',
        sources: ['local', 'url'],
        resourceType: resourceType ?? (isImageOnly ? 'image' : 'auto'),
        maxFileSize: 50 * 1024 * 1024, // 50MB
        ...(clientAllowedFormats ? { clientAllowedFormats } : {}),
      },
      (error: any, result: any) => {
        if (!error && result && result.event === 'success') {
          setImageInfo(result.info);
          if (onImageUploadRef.current) {
            onImageUploadRef.current(result.info);
          }
        }
      },
    );
    widgetRef.current = myWidget;
  };

  const setupWidgetLoop = () => {
    try {
      setupWidget();
    } catch (e) {
      setTimeout(() => {
        setupWidgetLoop();
      }, 1000);
    }
  };

  useEffect(() => {
    setupWidgetLoop();
  }, []);

  const thumbnailUrl = imageInfo?.thumbnail_url || initialUrl;
  let fileUrl = imageInfo?.url || initialUrl;
  if (fileUrl && !fileUrl.includes('https') && fileUrl.includes('http')) {
    fileUrl = fileUrl.replace('http', 'https');
  }
  const isImage =
    thumbnailUrl && thumbnailUrl.match(/\.(jpeg|jpg|gif|png)$/) != null;
  const hasFile = imageInfo || initialUrl;

  return (
    <div style={{ alignItems: 'center', display: 'flex' }}>
      <Helmet>
        <script
          src="https://upload-widget.cloudinary.com/2.10.2/global/all.js"
          type="text/javascript"
          defer
        ></script>
      </Helmet>
      <UploadImageButton
        id="upload_widget"
        className="file-block-btn"
        compact={compact}
        onClick={() => {
          if (isViewOnly) {
            if (fileUrl) {
              window.open(fileUrl, '_blank');
            }
            return;
          }
          if (!widgetRef.current) {
            setupWidget();
          }
          if (widgetRef.current) {
            widgetRef.current.open();
          }
        }}
      >
        {isImage ? (
          <img
            src={thumbnailUrl}
            style={{ maxWidth: '70px', maxHeight: '40px' }}
          />
        ) : hasFile ? (
          <svg
            className="db"
            viewBox="0 0 24 24"
            preserveAspectRatio="xMidYMid"
            style={{ fill: 'rgb(144, 173, 197)', width: '24px' }}
          >
            <path d="M4.8,1.77635684e-15 C3.48,1.77635684e-15 2.412,1.08 2.412,2.4 L2.4,21.6 C2.4,22.92 3.468,24 4.788,24 L19.2,24 C20.52,24 21.6,22.92 21.6,21.6 L21.6,7.2 L14.4,1.77635684e-15 L4.8,1.77635684e-15 Z M13.2,8.4 L13.2,1.8 L19.8,8.4 L13.2,8.4 Z"></path>
          </svg>
        ) : isViewOnly ? (
          t('cloudinary.noUploadedFile')
        ) : isImageOnly ? (
          'Upload image'
        ) : (
          'Upload file'
        )}
      </UploadImageButton>
      {hasFile ? (
        <>
          <a href={fileUrl} style={{ marginLeft: '10px' }} target="_blank" download>
            {imageInfo?.original_filename || initialFilename}
          </a>
          {!isViewOnly && (
            <IconButtonContainer>
              <IconButton
                onClick={() => {
                  if (onDelete) {
                    onDelete();
                  }
                  setImageInfo(null);
                }}
                title="Delete"
                aria-label="Delete"
                type={IconType.delete}
              />
            </IconButtonContainer>
          )}
        </>
      ) : !isViewOnly ? (
        <span style={{ marginLeft: '10px' }}>
          {/* {t('cloudinary.noUploadedFile')} */}
        </span>
      ) : null}
    </div>
  );
};

export default CloudinaryUploadWidget;
